import { Link, useParams } from "react-router-dom";
import { useEffect } from "react";
import { useI18n } from "../i18n";

const ServiceDetails = () => {
    const { id } = useParams();
    const { services, t } = useI18n();

    const service = services.items.find(
        (item) => String(item.slug ?? item.id) === id
    );

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, [id]);

    if (!service) {
        return (
            <main className="relative flex min-h-screen items-center justify-center overflow-hidden bg-slate-950 px-6 text-white">
                <div className="absolute -left-32 -top-32 h-96 w-96 rounded-full bg-sky-500/20 blur-[120px]" />
                <div className="absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-blue-600/20 blur-[120px]" />

                <div className="relative z-10 max-w-lg text-center">
                    <span className="mb-6 inline-flex h-20 w-20 items-center justify-center rounded-3xl border border-white/10 bg-white/5 text-4xl text-sky-400">
                        !
                    </span>

                    <p className="mb-3 text-sm font-semibold uppercase tracking-[0.3em] text-sky-400">
                        {t("serviceDetails.notFoundLabel")}
                    </p>

                    <h1 className="mb-4 text-3xl font-bold sm:text-4xl">
                        {t("serviceDetails.notFoundTitle")}
                    </h1>

                    <p className="mb-8 leading-7 text-slate-400">
                        {t("serviceDetails.notFoundMessage")}
                    </p>

                    <Link
                        to="/services"
                        className="group inline-flex items-center gap-3 rounded-full bg-sky-500 px-7 py-4 font-semibold text-white transition-all duration-300 hover:-translate-y-1 hover:bg-sky-400 hover:shadow-lg hover:shadow-sky-500/25"
                    >
                        <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            className="h-5 w-5 transition-transform duration-300 group-hover:-translate-x-1"
                        >
                            <path
                                d="M19 12H5m7 7-7-7 7-7"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                        {t("serviceDetails.returnHome")}
                    </Link>
                </div>
            </main>
        );
    }

    const description =
        service.fullInfo ?? service.description ?? service.info;

    return (
        <main className="min-h-screen overflow-hidden text-slate-950">
            {/* Hero */}
            <section className="group/hero relative flex min-h-[580px] items-end overflow-hidden bg-slate-950 pt-32 sm:min-h-[650px] lg:min-h-[760px]">
                {/* Фоновое изображение */}
                <img
                    key={service.photo}
                    src={service.photo}
                    alt={service.name}
                    fetchPriority="high"
                    className="absolute inset-0 h-full w-full object-cover animate-[heroZoom_18s_ease-out_forwards] transition-transform duration-1000 group-hover/hero:scale-105"
                />

                {/* Затемнение */}
                <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/55 to-slate-950/10" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/20 to-slate-950/10" />

                {/* Декоративное свечение */}
                <div className="pointer-events-none absolute -left-40 top-1/4 h-96 w-96 rounded-full bg-sky-500/15 blur-[130px]" />

                {/* Декоративная сетка */}
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 opacity-[0.08]"
                    style={{
                        backgroundImage:
                            "linear-gradient(rgba(255,255,255,.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.3) 1px, transparent 1px)",
                        backgroundSize: "80px 80px",
                        maskImage:
                            "linear-gradient(to bottom, black, transparent 85%)",
                    }}
                />

                {/* Контент */}
                <div className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-16 pt-10 md:px-12 md:pb-24 lg:pb-28">
                    <div className="max-w-5xl">
                        {/* Навигация */}
                        <Link
                            to="/services"
                            className="group/back mb-10 inline-flex items-center gap-3 rounded-full border border-white/15 bg-white/[0.07] px-5 py-3 text-sm font-medium text-white/85 backdrop-blur-xl transition-all duration-300 hover:border-sky-400/50 hover:bg-white/10 hover:text-white animate-[fadeUp_.7s_ease-out_both]"
                        >
                            <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                className="h-4 w-4 transition-transform duration-300 group-hover/back:-translate-x-1"
                            >
                                <path
                                    d="M19 12H5m7 7-7-7 7-7"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                            {t("serviceDetails.home")}
                        </Link>

                        {/* Надзаголовок */}
                        <div className="mb-6 flex items-center gap-4 animate-[fadeUp_.8s_.1s_ease-out_both]">
                            <span className="h-px w-12 bg-sky-400" />
                            <span className="text-xs font-bold uppercase tracking-[0.28em] text-sky-300 sm:text-sm">
                                {t("serviceDetails.sectionTitle")}
                            </span>
                        </div>

                        {/* Заголовок */}
                        <h1 className="max-w-4xl text-3xl font-extrabold leading-[1.08] tracking-tight text-white animate-[fadeUp_.9s_.2s_ease-out_both] sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl">
                            {service.name}
                        </h1>

                        {/* Краткое описание */}
                        {service.info && (
                            <p className="mt-7 max-w-2xl text-base leading-7 text-white/75 animate-[fadeUp_.9s_.35s_ease-out_both] sm:text-lg sm:leading-8 md:text-xl">
                                {service.info}
                            </p>
                        )}

                        {/* Нижний индикатор */}
                        <div className="mt-10 flex  items-center gap-4 animate-[fadeUp_.9s_.45s_ease-out_both]">
                            <span className="relative flex h-3 w-3">
                                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sky-400 opacity-60" />
                                <span className="relative inline-flex h-3 w-3 rounded-full bg-sky-400" />
                            </span>

                            <span className="text-xs font-medium uppercase tracking-[0.2em] text-white/60">
                                {t("serviceDetails.moreInfo")}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Плавный переход к следующей секции */}
            </section>

            {/* Основная информация */}
            <section className="relative px-6 py-20 md:px-12 md:py-28 lg:py-32">
                {/* Декоративное свечение */}
                <div className="pointer-events-none absolute right-0 top-20 h-72 w-72 rounded-full bg-sky-100/70 blur-[100px]" />

                <div className="relative mx-auto max-w-7xl">
                    <div className="grid gap-14 lg:grid-cols-[0.7fr_1.3fr] lg:gap-24">
                        {/* Левая колонка */}
                        <div className="animate-[fadeUp_.8s_ease-out_both] rounded-2xl
                  border border-white/80 bg-white/80 p-5
                  shadow-sm backdrop-blur-md h-max">
                            <div className="mb-7 inline-flex items-center gap-3 rounded-full border border-sky-100 bg-sky-50 px-4 py-2">
                                <span className="h-2 w-2 rounded-full bg-sky-500" />
                                <span className="text-xs font-bold uppercase tracking-[0.18em] text-sky-700">
                                    {t("serviceDetails.infoLabel")}
                                </span>
                            </div>

                            <h2 className="max-w-md text-3xl font-extrabold leading-tight tracking-tight text-slate-950 sm:text-4xl md:text-5xl">
                                {t("serviceDetails.infoHeading")}
                                <span className="mt-1 block text-sky-500">
                                    {t("serviceDetails.infoHeadingAccent")}
                                </span>
                            </h2>

                            <div className="mt-8 h-1 w-16 rounded-full bg-gradient-to-r from-sky-500 to-blue-300" />

                            <p className="mt-7 max-w-sm text-base leading-7 text-slate-500">
                                {t("serviceDetails.infoIntro")}
                            </p>
                        </div>

                        {/* Правая колонка */}
                        <div className="animate-[fadeUp_.9s_.15s_ease-out_both]">
                            <div className="relative overflow-hidden rounded-3xl border border-slate-200/80 bg-white p-7 shadow-[0_20px_80px_-35px_rgba(15,23,42,0.18)] sm:p-10 md:p-12">
                                {/* Декоративный элемент */}
                                <div className="absolute right-0 top-0 h-32 w-32 rounded-bl-full bg-gradient-to-bl from-sky-50 to-transparent" />

                                <div className="relative">
                                    <div className="mb-8 flex items-center gap-4">
                                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-sky-50 text-sky-600">
                                            <svg
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                className="h-6 w-6"
                                            >
                                                <path
                                                    d="M12 3v18M3 12h18"
                                                    stroke="currentColor"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                />
                                            </svg>
                                        </div>

                                        <div>
                                            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                                                {t("serviceDetails.serviceLabel")}
                                            </p>
                                            <h3 className="mt-1 text-lg font-bold text-slate-900 sm:text-xl">
                                                {service.name}
                                            </h3>
                                        </div>
                                    </div>

                                    <div className="mb-8 h-px w-full bg-gradient-to-r from-slate-200 via-slate-100 to-transparent" />

                                    <p className="whitespace-pre-line text-base leading-8 text-slate-600 sm:text-lg sm:leading-9">
                                        {description || t("serviceDetails.infoComingSoon")}
                                    </p>
                                    {service.features?.length > 0 && (
                                        <ul className="mt-6 space-y-3 text-base leading-7 text-slate-600 sm:text-lg sm:leading-8">
                                            {service.features.map((feature) => (
                                                <li key={feature} className="flex gap-3">
                                                    <span
                                                        aria-hidden="true"
                                                        className="mt-3 h-2 w-2 shrink-0 rounded-full bg-sky-500"
                                                    />
                                                    <span>{feature}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>

                            {/* Нижняя декоративная подпись */}
                            <div className="mt-6 flex items-center gap-3 px-2">
                                <span className="h-px w-8 bg-sky-400" />
                                <span className="text-xs font-medium tracking-wide text-slate-400">
                                    {t("serviceDetails.qualityTagline")}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Финальный CTA */}
            <section className="px-6 pb-20 md:px-12 md:pb-28">
                <div className="group relative mx-auto max-w-7xl overflow-hidden rounded-[2rem] bg-slate-950 px-7 py-12 sm:px-12 sm:py-16 lg:px-16">
                    {/* Фоновые эффекты */}
                    <div className="pointer-events-none absolute -right-20 -top-32 h-80 w-80 rounded-full bg-sky-500/20 blur-[100px] transition-transform duration-700 group-hover:scale-125" />
                    <div className="pointer-events-none absolute -bottom-32 left-1/3 h-64 w-64 rounded-full bg-blue-600/15 blur-[100px]" />

                    <div className="relative flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
                        <div className="max-w-2xl">
                            <p className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-sky-400">
                                {t("serviceDetails.questions")}
                            </p>

                            <h2 className="text-3xl font-bold leading-tight text-white sm:text-4xl lg:text-5xl">
                                {t("serviceDetails.discussProject")}
                            </h2>

                            <p className="mt-5 max-w-xl text-base leading-7 text-slate-400">
                                {t("serviceDetails.contactDescription")}
                            </p>
                        </div>

                        <Link
                            to="/contact"
                            className="group/button inline-flex shrink-0 items-center gap-4 rounded-full bg-sky-500 px-7 py-4 font-semibold text-white shadow-lg shadow-sky-500/20 transition-all duration-300 hover:-translate-y-1 hover:bg-sky-400 hover:shadow-xl hover:shadow-sky-500/30 sm:px-8 sm:py-5"
                        >
                            {t("serviceDetails.contactUs")}

                            <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                className="h-5 w-5 transition-transform duration-300 group-hover/button:translate-x-1"
                            >
                                <path
                                    d="M5 12h14m-6-6 6 6-6 6"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </Link>
                    </div>
                </div>
            </section>

            {/* Локальные анимации */}
            <style>{`
                @keyframes fadeUp {
                    from {
                        opacity: 0;
                        transform: translateY(24px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                @keyframes heroZoom {
                    from {
                        transform: scale(1.08);
                    }
                    to {
                        transform: scale(1);
                    }
                }

                @media (prefers-reduced-motion: reduce) {
                    *,
                    *::before,
                    *::after {
                        animation-duration: 0.01ms !important;
                        animation-iteration-count: 1 !important;
                        scroll-behavior: auto !important;
                        transition-duration: 0.01ms !important;
                    }
                }
            `}</style>
        </main>
    );
};

export default ServiceDetails;